import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Space, Typography } from 'antd';
import React, { useEffect, useMemo } from 'react';
import DrawerForm from './DrawerForm';
import TableWithActions from './TableWithActions';

interface WastageSectionProps {
  data: any[];
  visible: boolean;
  editingRecord: any;
  recipeDetails: any | null;
  onEdit: (record: any) => void;
  onDelete: (key: string) => void;
  onSubmit: (values: any) => void;
  onClose: () => void;
  disabled?: boolean;
}

const { Text } = Typography;

const WastageSection: React.FC<WastageSectionProps> = ({
  data,
  visible,
  editingRecord,
  recipeDetails,
  onEdit,
  onDelete,
  onSubmit,
  onClose,
  disabled,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (editingRecord) {
      form.setFieldsValue(editingRecord);
    } else {
      form.resetFields();
    }
  }, [editingRecord, form]);

  // Calculate total wastage cost using useMemo (optimized for performance)
  const totalWastageCost = useMemo(() => {
    return data.reduce((sum, item) => sum + (Number(item.cost) || 0), 0);
  }, [data]);

  const handleSubmit = (values: any) => {
    const selectedMaterial = materialOptions.find(
      (option: { value: any }) => option.value === values.material
    );

    const newWastageEntry = {
      ...values,
      material: selectedMaterial ? selectedMaterial.label : values.material, // Store the name instead of ID
      id: values.material,
    };

    onSubmit(newWastageEntry);
    form.resetFields();
  };

  // Get the maximum allowed quantity for the selected material
  const getMaxQuantity = (materialId: string) => {
    if (!recipeDetails?.usedProductRecipes) return undefined;

    const recipeItem = recipeDetails.usedProductRecipes.find(
      (recipe: any) => recipe.usedVariantId === materialId
    );

    return recipeItem ? Number(recipeItem.usedProductQuantity) : undefined;
  };

  // Create validator function for quantity
  const validateQuantity = (rule: any, value: number, callback: any) => {
    const materialId = form.getFieldValue('material');
    if (!materialId) {
      callback('Please select a material first');
      return;
    }

    const maxQuantity = getMaxQuantity(materialId);
    if (maxQuantity !== undefined && value > maxQuantity) {
      callback(`Quantity cannot exceed ${maxQuantity} (recipe quantity)`);
      return;
    }

    callback();
  };

  // Extract materials from recipeDetails to show in dropdown
  const materialOptions =
    recipeDetails?.usedProductRecipes.map((recipe: any) => ({
      label: recipe.usedVariantName,
      value: recipe.usedVariantId,
      maxQuantity: recipe.usedProductQuantity,
    })) || [];

  return (
    <div>
      <Space
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h3>Wastage</h3>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => onEdit(null)}
          disabled={disabled}
        >
          Add Wastage
        </Button>
      </Space>
      <TableWithActions
        columns={[
          { title: 'Material', dataIndex: 'material', key: 'material' },
          { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
          { title: 'Notes', dataIndex: 'notes', key: 'notes' },
          { title: 'Wastage Cost', dataIndex: 'cost', key: 'cost' },
        ]}
        data={data}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {/* Total Wastage Cost */}
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Text strong style={{ fontSize: 16 }}>
          Total Wastage Cost: {totalWastageCost.toFixed(2)}
        </Text>
      </div>
      <DrawerForm
        visible={visible}
        title={editingRecord ? 'Edit Wastage' : 'Add Wastage'}
        form={form}
        onClose={onClose}
        onSubmit={handleSubmit}
        fields={[
          {
            name: 'material',
            label: 'Material',
            type: 'select',
            options: materialOptions,
            rules: [{ required: true, message: 'Please select a material' }],
          },
          {
            name: 'quantity',
            label: 'Quantity',
            type: 'number',
            rules: [
              { required: true, message: 'Please enter quantity' },
              { validator: validateQuantity },
            ],
          },
          { name: 'cost', label: 'Wastage Cost', type: 'number' },
          { name: 'notes', label: 'Notes', type: 'textarea' },
        ]}
      />
    </div>
  );
};

export default WastageSection;
